import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import styled from 'styled-components';
import axios from 'axios';
import Wrapper from './Wrapper';
import Avatar from "./Avatar";
import FeedbackForm from "./FeedbackForm";
import ModalAlert from "./ModalAlert";
import { useTranslation } from "react-i18next";
import { MdVisibility, MdVisibilityOff, MdArrowBack } from 'react-icons/md';
import { fetchLanguages } from "../context/LanguageUtility";
import SubscriptionStatus from "./SubscriptionStatus";
import { useChat } from "../context/ChatProvider";
import { useNavigate } from 'react-router-dom';
import { BeatLoader } from 'react-spinners';

/* ─── layout ─── */

const PageContainer = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    height: 100%;
    margin: auto 0;
    overflow-y: auto;
    min-width: 0;

    /* custom scrollbar */
    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }

    @media (max-width: 820px) {
        height: 100%;
    }
`;

const ContentArea = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    background: rgba(15, 12, 41, 0.6);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    overflow-y: auto;
    padding: 0 0 2rem;
`;

/* ─── header ─── */

const Header = styled.header`
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.03);
    flex-shrink: 0;
`;

const BackButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.4em;
    height: 2.4em;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.08);
    cursor: pointer;
    transition: all 0.2s ease;
    color: rgba(255, 255, 255, 0.65);
    flex-shrink: 0;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: white;
    }
`;

const HeaderInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    flex: 1;
    min-width: 0;
`;

const HeaderTitle = styled.h2`
    font-size: 0.95rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
`;

const HeaderSub = styled.span`
    font-size: 0.78rem;
    color: rgba(255, 255, 255, 0.4);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

/* ─── sections ─── */

const Section = styled.div`
    margin: 1.2rem 1.5rem 0;
    padding: 1.4rem;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 16px;
    backdrop-filter: blur(8px);
`;

const SectionTitle = styled.h3`
    font-size: 0.85rem;
    font-weight: 600;
    color: #c7d2fe;
    margin-bottom: 1rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
`;

/* ─── form elements ─── */

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
`;

const Label = styled.label`
    font-size: 0.82rem;
    color: rgba(255, 255, 255, 0.5);
    font-weight: 500;
`;

const Input = styled.input`
    width: 100%;
    border: 1px solid rgba(255, 255, 255, 0.08);
    padding: 0.7rem 1rem;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
    font-family: 'Poppins', sans-serif;
    transition: border-color 0.2s, background 0.2s;

    &::placeholder { color: rgba(255, 255, 255, 0.25); }
    &:focus {
        outline: none;
        border-color: rgba(99, 102, 241, 0.5);
        background: rgba(255, 255, 255, 0.08);
    }
`;

const Select = styled.select`
    width: 100%;
    border: 1px solid rgba(255, 255, 255, 0.08);
    padding: 0.7rem 1rem;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
    font-family: 'Poppins', sans-serif;
    -webkit-appearance: none;
    transition: border-color 0.2s;

    &:focus {
        outline: none;
        border-color: rgba(99, 102, 241, 0.5);
    }

    & option {
        background: #1a1a2e;
        color: white;
    }

    @media (max-width: 768px) {
        padding: 1rem;
    }
`;

const PasswordWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const PasswordToggle = styled.span`
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
    cursor: pointer;
    color: rgba(255, 255, 255, 0.3);
    font-size: 1.3rem;
    transition: color 0.2s;

    &:hover { color: rgba(255, 255, 255, 0.6); }
`;

const SaveButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.65rem 1.8rem;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: white;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 3px 14px rgba(99, 102, 241, 0.3);
    margin-top: 0.5rem;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 5px 20px rgba(99, 102, 241, 0.45);
    }
    &:active { transform: translateY(0); }
`;

const ErrorText = styled.p`
    font-size: 0.78rem;
    color: #fca5a5;
    margin: 0;
    line-height: 1.5;
`;

const LoadingOverlay = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 3rem 0;
`;

/* ─── component ─── */

const AccountPage = () => {
    const { register, handleSubmit, formState: { errors }, setValue } = useForm();
    const [isLoading, setIsLoading] = useState(false);
    const [userData, setUserData] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState({ title: '', message: '', isSuccess: false });
    const [showPassword, setShowPassword] = useState(false);
    const [supportedLanguages, setSupportedLanguages] = useState([]);
    const [languageConfiguration, setLanguageConfiguration] = useState(null);
    const { user, setUser, setBlockSending, setIsNavOpen, setActiveView } = useChat();

    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        async function fetchUserData() {
            try {
                const response = await axios.get(`${process.env.REACT_APP_SERVER}/account`, { withCredentials: true });
                if (response.data) {
                    setUserData(response.data);
                    setValue("language", response.data.language);
                    user.subscriber = response.data.subscriber;
                    setUser(user);
                    if (user.subscriber) setBlockSending(false);
                }
            } catch (error) {
                console.error("Error fetching user details:", error);
            }
        }
        setIsLoading(true);
        fetchLanguages(setSupportedLanguages, setLanguageConfiguration);
        fetchUserData();
        setIsLoading(false);
    }, []);

    const refreshAccountData = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_SERVER}/account`, { withCredentials: true });
            if (response.data) {
                setUserData(response.data);
                setValue("language", response.data.language);
                user.subscriber = response.data.subscriber;
                setUser(user);
                if (user.subscriber) setBlockSending(false);
            }
        } catch (error) {
            console.error("Error fetching user details:", error);
        }
    };

    const handleCancelSub = async () => {
        setIsLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_SERVER}/sub-cancel`, {}, { withCredentials: true });
            if (response.data.success) {
                setModalContent({ title: 'Cancelled', message: 'Your subscription has been successfully cancelled.', isSuccess: true });
            } else {
                setModalContent({ title: 'Failed', message: `${t('RequestFailed')}`, isSuccess: false });
            }
        } catch (error) {
            setModalContent({ title: 'Failed', message: `${t('RequestFailed')}`, isSuccess: false });
        }
        setIsLoading(false);
        setIsModalOpen(true);
    };

    const onSubmit = async (data) => {
        const { password, language } = data;
        let payload = { language };
        if (password) payload.password = password;

        setIsLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_SERVER}/account`, payload, { withCredentials: true });
            if (response.data.success) {
                setModalContent({ title: `${t('SuccessTitle')}`, message: `${t('AccountUpdatedSuccess')}`, isSuccess: true });
                setValue("password", "");
            } else {
                setModalContent({ title: 'Failed', message: `${t('RequestFailed')}`, isSuccess: false });
            }
        } catch (error) {
            setModalContent({ title: 'Failed', message: `${t('RequestFailed')}`, isSuccess: false });
        }
        setIsModalOpen(true);
        setIsLoading(false);
    };

    const handleBackClick = () => {
        setIsNavOpen(true);
        setActiveView("chat");
        window.scrollTo(0, 0);
        navigate('/chat');
    };

    return (
        <Wrapper restrictGuest={true}>
            <PageContainer>
                <ContentArea>

                    {/* Header */}
                    <Header>
                        <BackButton onClick={handleBackClick}>
                            <MdArrowBack size="1.2em" />
                        </BackButton>
                        {userData.avatar && <Avatar src={userData.avatar} size="2.8em" />}
                        <HeaderInfo>
                            <HeaderTitle>{userData.username || t('AccountTitle')}</HeaderTitle>
                            <HeaderSub>{userData.email}</HeaderSub>
                        </HeaderInfo>
                    </Header>

                    {isLoading ? (
                        <LoadingOverlay>
                            <BeatLoader color="#a5b4fc" size={12} />
                        </LoadingOverlay>
                    ) : (
                        <>
                            {/* Subscription */}
                            <SubscriptionStatus user={userData} onCancelSub={handleCancelSub} />

                            {/* Account Settings */}
                            <Section>
                                <SectionTitle>{t('AccountTitle')}</SectionTitle>
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <FormGroup>
                                        <Label>{t('AccountPagePassword')}</Label>
                                        <PasswordWrapper>
                                            <Input
                                                type={showPassword ? "text" : "password"}
                                                placeholder="••••••••"
                                                maxLength={50}
                                                {...register("password", {
                                                    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{7,}$/
                                                })}
                                            />
                                            <PasswordToggle onClick={() => setShowPassword(!showPassword)}>
                                                {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                                            </PasswordToggle>
                                        </PasswordWrapper>
                                        {errors.password && (
                                            <ErrorText>
                                                Password must be at least 7 characters with uppercase, lowercase, and a number.
                                            </ErrorText>
                                        )}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>{t('AccountPageLanguageChange')}</Label>
                                        <Select {...register("language", { required: true })}>
                                            {supportedLanguages.map((language, index) => (
                                                <option key={index} value={language}>{t(language)}</option>
                                            ))}
                                        </Select>
                                        {errors.language && <ErrorText>{t('SignupLanguageValidate')}</ErrorText>}
                                    </FormGroup>

                                    <SaveButton type="submit">{t('SaveChangesButton')}</SaveButton>
                                </form>
                            </Section>

                            {/* Feedback */}
                            <FeedbackForm />
                        </>
                    )}

                    <ModalAlert
                        isOpen={isModalOpen}
                        onClose={() => { setIsModalOpen(false); refreshAccountData(); }}
                        title={modalContent.title}
                        message={modalContent.message}
                        isSuccess={modalContent.isSuccess}
                    />

                </ContentArea>
            </PageContainer>
        </Wrapper>
    );
};

export default AccountPage;
